import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Stack from '@mui/material/Stack'

import { skipToken } from '@reduxjs/toolkit/query'

import { ConsultationType } from '@/core/models/consultation.model'
import { useGetConsultationQuery } from '@/core/services/consultation'

import { isStaff } from '@/utils/type-guards'

import QueryBoundary from '@/components/ui/template/QueryBoundary'
import NotFoundPage from '@/pages/error/NotFoundPage'

import ChimeMeeting from '@/pages/diagnostic/containers/ChimeMeeting'

import { useConsultationData } from '../model/useConsultationData'
import { useConsultationLifecycle } from '../model/useConsultationLifecycle'
import { useParticipants } from '../model/useParticipants'
import { useJoinMeeting } from '../model/useJoinMeeting'

import { Topbar } from './Topbar'

type ConsultationMeetingPageProps = {}

export function ConsultationMeetingPage(props: ConsultationMeetingPageProps) {
  const navigate = useNavigate()

  // Get consultation data
  const { consultationId, authUser, consultation, participants, unreadChatMessages } = useConsultationData()
  const { isLoading, isError, error } = useGetConsultationQuery(consultationId ?? skipToken)

  // Manage consultation lifecycle (socket events)
  useConsultationLifecycle({ consultationId })

  // Get participant info
  const { isDoctorOnline, isPdsOnline, doctor, pds } = useParticipants({ consultation, participants })

  // Handle meeting join
  const { handleJoinMeeting } = useJoinMeeting({ consultationId, authUser })

  // Handle navigation when closing consultation
  const handleCloseConsultation = () => {
    navigate('/')
  }

  // Early returns for loading/error states
  if (!authUser || !isStaff(authUser)) return null

  if (!consultation) {
    return <QueryBoundary isLoading={isLoading} isError={isError} error={error} fallback={null} />
  }

  if (consultation.type !== ConsultationType.MeetingOnly) {
    return <NotFoundPage />
  }

  return (
    <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
      <Topbar
        authUser={authUser}
        consultation={consultation}
        doctor={doctor}
        pds={pds}
        isDoctorOnline={isDoctorOnline}
        isPdsOnline={isPdsOnline}
        unreadChatMessages={unreadChatMessages}
        onCloseConsultation={handleCloseConsultation}
      />
      <Stack sx={{ flex: 1, height: '100%', '& > div': { flex: 1 } }}>
        <ChimeMeeting disableScreenshot joinMeeting={handleJoinMeeting} />
      </Stack>
    </Stack>
  )
}
